import React, { useEffect, useRef, useState } from 'react';

/**
 * CountUp — animates from 0 to `value` when the element scrolls into view.
 * Preserves any non-numeric suffix (e.g. "+" in "30+").
 */
export default function CountUp({ value, duration = 1800, className = '', style }) {
  const rawStr = String(value);
  const match = rawStr.match(/^(\d+)(.*)$/);
  const targetNum = match ? parseInt(match[1], 10) : 0;
  const suffix = match ? match[2] : '';

  const [display, setDisplay] = useState(0);
  const [started, setStarted] = useState(false);
  const nodeRef = useRef(null);

  useEffect(() => {
    if (!nodeRef.current || started) return undefined;
    const el = nodeRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setStarted(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.4 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return undefined;
    const start = performance.now();
    let frame;
    const step = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // easeOutExpo for a smooth, luxurious feel
      const eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      setDisplay(Math.round(eased * targetNum));
      if (progress < 1) {
        frame = requestAnimationFrame(step);
      }
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [started, targetNum, duration]);

  return (
    <span ref={nodeRef} className={className} style={style}>
      {display}{suffix}
    </span>
  );
}
